/**
 * Invitations API wrapper. Mirrors `apps/api/src/modules/invitations` routes.
 */
import type { invitations } from "@fynd-onshelf/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";

import type { APIError } from "../http.js";
import { qk } from "../query-keys.js";

import { apiRequest } from "./_client.js";
import type { UserRole } from "./users.js";

export interface CreateInvitationRequest {
  email: string;
  // tenant_admin is invitable, but only by a tenant_admin caller (server
  // enforces via assertCanInvite; the web invite policy mirrors it).
  role: UserRole;
  /**
   * Public storeIds the invitee will be bound to on accept. Required when
   * role is store-scoped (store_manager / store_associate / field_auditor).
   * Must be omitted or [] for tenant-wide roles.
   */
  storeIds?: string[];
}

export interface CreateInvitationResponse {
  ok: true;
  invitationId: string;
  /** Present only in dev when email send was skipped due to unset webhook URL. */
  devLink?: string;
}

export interface AcceptInvitationResponse {
  ok: true;
  tenantId: string;
  /** Joined tenant's URL code — hard-navigate to /t/<code>/dashboard with it. */
  tenantCode: string;
}

export type InvitationStatus = "pending" | "accepted" | "cancelled" | "expired";

export interface InvitationSummary {
  id: string;
  email: string;
  role: UserRole;
  /** Public storeIds bound to this invitation; empty for tenant-wide. */
  storeIds: string[];
  invitedByEmail: string | null;
  status: InvitationStatus;
  createdAt: string;
  expiresAt: string;
}

export interface ListInvitationsResponse {
  items: InvitationSummary[];
}

export interface RevokeInvitationResponse {
  ok: true;
}

export interface ReissueInvitationResponse {
  ok: true;
  invitationId: string;
  /** Fresh accept link — copy it; the raw token isn't stored anywhere else. */
  inviteLink: string;
  emailSent: boolean;
}

export type InvitationLookupResponse = invitations.InvitationLookupResponse;
export type InvitationLookupStatus = InvitationLookupResponse["status"];

export const invitationsApi = {
  create: (data: CreateInvitationRequest): Promise<CreateInvitationResponse> =>
    apiRequest({ method: "POST", url: "/api/v2/company-invitations", data }),

  accept: (token: string): Promise<AcceptInvitationResponse> =>
    apiRequest({
      method: "POST",
      url: `/api/v2/company-invitations/${encodeURIComponent(token)}/accept`,
    }),

  list: (signal?: AbortSignal): Promise<ListInvitationsResponse> =>
    apiRequest({ method: "GET", url: "/api/v2/company-invitations", signal }),

  revoke: (publicId: string): Promise<RevokeInvitationResponse> =>
    apiRequest({
      method: "DELETE",
      url: `/api/v2/company-invitations/${encodeURIComponent(publicId)}`,
    }),

  reissue: (publicId: string): Promise<ReissueInvitationResponse> =>
    apiRequest({
      method: "POST",
      url: `/api/v2/company-invitations/${encodeURIComponent(publicId)}/reissue`,
    }),

  lookup: (token: string, signal?: AbortSignal): Promise<InvitationLookupResponse> =>
    apiRequest({
      method: "GET",
      url: "/api/v2/company-invitations/lookup",
      params: { token },
      signal,
    }),
};

export function useCreateInvitationMutation(
  options?: Omit<
    UseMutationOptions<CreateInvitationResponse, APIError, CreateInvitationRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<CreateInvitationResponse, APIError, CreateInvitationRequest>({
    ...options,
    mutationFn: invitationsApi.create,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.invitations.list() });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useAcceptInvitationMutation(
  options?: Omit<UseMutationOptions<AcceptInvitationResponse, APIError, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<AcceptInvitationResponse, APIError, string>({
    ...options,
    mutationFn: (token: string) => invitationsApi.accept(token),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      // Server re-minted the auth cookie with the new tenant claims; force
      // a fresh /auth/me + companies list so the SPA picks up the new
      // tenantId/role/accessibleStores before any guarded route renders.
      void qc.invalidateQueries({ queryKey: qk.auth.me });
      void qc.invalidateQueries({ queryKey: qk.users.myCompanies });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useInvitationsListQuery(
  options?: Omit<UseQueryOptions<ListInvitationsResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<ListInvitationsResponse, APIError>({
    queryKey: qk.invitations.list(),
    queryFn: ({ signal }) => invitationsApi.list(signal),
    retry: false,
    ...options,
  });
}

/**
 * Token-gated lookup shared by acceptance and authentication email prefill.
 * Caches per token; the lookup never mutates server state.
 */
export function useInvitationLookupQuery(
  token: string | null,
  options?: Omit<UseQueryOptions<InvitationLookupResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<InvitationLookupResponse, APIError>({
    queryKey: ["invitations", "lookup", token ?? ""],
    queryFn: ({ signal }) => invitationsApi.lookup(token!, signal),
    enabled: !!token && token.length >= 16 && token.length <= 255,
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useRevokeInvitationMutation(
  options?: Omit<UseMutationOptions<RevokeInvitationResponse, APIError, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<RevokeInvitationResponse, APIError, string>({
    ...options,
    mutationFn: (publicId: string) => invitationsApi.revoke(publicId),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.invitations.list() });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useReissueInvitationMutation(
  options?: Omit<UseMutationOptions<ReissueInvitationResponse, APIError, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<ReissueInvitationResponse, APIError, string>({
    ...options,
    mutationFn: (publicId: string) => invitationsApi.reissue(publicId),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.invitations.list() });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}
